import {
  Button,
  DataTableSortByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  ExpandableSearch,
  Icon,
  Separator,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type DataTableGroupSortDirection,
  type Omittable,
} from "@salestrak/ui";
import {
  ASSIGNED_SORT_LABEL,
  assignedSortDirection,
  type AssignedSort,
} from "./use-assigned-list";

function isAssignedSort(value: string): value is AssignedSort {
  return value === "updated" || value === "created" || value === "name";
}

/**
 * The card body's toolbar band — the full-page table's band, on the toolbar
 * grammar every list wears. Narrowing on the right, in the cross-view order:
 * the Sort by dropdown (the shared field popover with its direction footer)
 * and search. Sort is a per-viewer lens over this card, held in its view-state
 * and seeded from Settings; search is plain state. After a hairline, the gear
 * opens the card's Customize view — the workspace list's own drawer, pinned
 * inside the card (`AssignedCustomizePanel`) — where layout, the Type filter
 * and the saved sort live; a popover of toggles beside the table is not a
 * grammar this app uses.
 */
export function AssignedToolbar({
  sort,
  sortDirection,
  defaultSort,
  onSortChange,
  onSortDirectionChange,
  searchValue,
  onSearchChange,
  onCustomize,
}: Omittable<{
  sort: AssignedSort;
  sortDirection: DataTableGroupSortDirection;
  /** The Settings sort — where the "—" clear row returns the lens to. */
  defaultSort: AssignedSort;
  /** A field pick resets the direction to the field's natural one. */
  onSortChange: (value: AssignedSort, direction: DataTableGroupSortDirection) => void;
  onSortDirectionChange: (value: DataTableGroupSortDirection) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Opens the card's Customize view. */
  onCustomize: () => void;
}>) {
  const atDefault = sort === defaultSort && sortDirection === assignedSortDirection(defaultSort);
  return (
    <DataTableToolbar>
      <DataTableToolbarActions>
        {/* The card always has an order, so the chip lights only when the
            viewer's lens LEAVES the Settings order: at the designed order it
            wears the unsorted face (icon alone), and the "—" row is the way
            back to it. A chip lit at rest read as a filter applied. */}
        <DataTableSortByDropdown
          sortByField={atDefault ? "none" : sort}
          setSortByField={(field) => {
            const next = isAssignedSort(field) ? field : defaultSort;
            onSortChange(next, assignedSortDirection(next));
          }}
          sortByLabels={ASSIGNED_SORT_LABEL}
          sortDirection={sortDirection}
          setSortDirection={onSortDirectionChange}
        />
        {/* The bare library search, not `ViewSearch`: a dashboard can hold
            several cards, so none may claim the `/` hotkey. */}
        <ExpandableSearch
          size="xs"
          value={searchValue}
          onValueChange={onSearchChange}
          tooltip="Search what's assigned to you"
        />
        <Separator orientation="vertical" className="h-4" />
        {/* The workflow view's own trigger (`ViewOptionsTrigger`): outline
            pill on the toolbar's `icon-xs` rung, the same tooltip words. */}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                aria-label="Customize view"
                onClick={onCustomize}
                shape="pill"
                size="icon-xs"
                variant="outline"
              />
            }
          >
            <Icon name="settings" size="sm" />
          </TooltipTrigger>
          <TooltipPopup side="top">Customize your view settings</TooltipPopup>
        </Tooltip>
      </DataTableToolbarActions>
    </DataTableToolbar>
  );
}
